import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface AiAppPolicyProps {
  apiUrl: string;
}

type PolicyAction = 'log' | 'alert' | 'block';

interface Provider {
  id: string;
  label: string;
  kind?: string;
  type?: 'llm' | 'mcp';
  riskLevel?: 'sanctioned' | 'tolerated' | 'unsanctioned' | 'high_risk';
  match: string[];
  // Process names the agent terminates in Block mode (desktop clients only).
  apps?: string[];
}

const RISK_BADGE: Record<string, string> = {
  sanctioned: 'badge-success',
  tolerated: 'badge-info',
  unsanctioned: 'badge-unknown',
  high_risk: 'badge-error',
};

const RISK_LABEL: Record<string, string> = {
  sanctioned: 'Sanctioned',
  tolerated: 'Tolerated',
  unsanctioned: 'Unsanctioned',
  high_risk: 'High Risk',
};

const ACTIONS: PolicyAction[] = ['log', 'alert', 'block'];

interface ShadowAiParseResult {
  action: PolicyAction;
  providerIds: string[];
  warnings: string[];
  source: 'deterministic';
}

const AiAppPolicy: React.FC<AiAppPolicyProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<PolicyAction>('block');
  const [currentApps, setCurrentApps] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [commandText, setCommandText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState<ShadowAiParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [provRes, policyRes] = await Promise.all([
        fetch(`${apiUrl}/admin/discovery/providers`).then((r) => r.json()),
        fetch(`${apiUrl}/admin/bdr/policy`).then((r) => r.json()),
      ]);
      const provs: Provider[] = Array.isArray(provRes.providers) ? provRes.providers : [];
      setProviders(provs);
      setAction((policyRes.action as PolicyAction) || 'block');
      setCurrentApps(Array.isArray(policyRes.apps) ? policyRes.apps : []);

      const endpointSet = new Set<string>((Array.isArray(policyRes.endpoints) ? policyRes.endpoints : []).map((d: string) => d.toLowerCase()));
      const appSet = new Set<string>((Array.isArray(policyRes.apps) ? policyRes.apps : []).map((a: string) => a.toLowerCase()));
      // A provider is preselected when its domains are all in the saved policy,
      // OR its process names are all in the saved kill list — so app-carrying
      // (desktop) entries round-trip on reload, not just domain-only ones.
      const preselected = new Set<string>(
        provs
          .filter((p) => {
            const domainsCovered = p.match.length > 0 && p.match.every((m) => endpointSet.has(m.toLowerCase()));
            const appsCovered = (p.apps || []).length > 0 && (p.apps || []).every((a) => appSet.has(a.toLowerCase()));
            return domainsCovered || appsCovered;
          })
          .map((p) => p.id)
      );
      setSelected(preselected);
    } catch (err) {
      console.error('Failed to load AI app policy:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectByRisk = (risk: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      providers.filter((p) => p.riskLevel === risk).forEach((p) => next.add(p.id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const runParseCommand = async () => {
    const trimmed = commandText.trim();
    if (!trimmed) return;
    setParsing(true);
    setParseError(null);
    try {
      const r = await fetch(`${apiUrl}/admin/discovery/parse-shadow-policy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: trimmed }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setParseError(data.message || 'Failed to parse command');
        return;
      }
      const result: ShadowAiParseResult = await r.json();
      setParseResult(result);
    } catch (err) {
      console.error(err);
      setParseError('Error parsing command. Make sure the firewall is running.');
    } finally {
      setParsing(false);
    }
  };

  // Preview-only until this — the parser never writes. Applying just loads the
  // parsed action + providers into the same selection/action state the manual
  // checklist above uses, so the existing Save button (and its existing
  // single-flat-action semantics) is what actually persists the policy.
  const applyParseResult = () => {
    if (!parseResult) return;
    setAction(parseResult.action);
    setSelected(new Set(parseResult.providerIds));
    setSavedNotice(null);
  };

  const selectedDomains = useMemo(() => {
    const domains = new Set<string>();
    providers
      .filter((p) => selected.has(p.id))
      .forEach((p) => p.match.forEach((m) => domains.add(m.toLowerCase())));
    return Array.from(domains).sort();
  }, [providers, selected]);

  // Process names to terminate, derived from the current selection (mirrors
  // selectedDomains). Empty for cloud-only providers with no desktop client.
  const selectedApps = useMemo(() => {
    const apps = new Set<string>();
    providers
      .filter((p) => selected.has(p.id))
      .forEach((p) => (p.apps || []).forEach((a) => apps.add(a)));
    return Array.from(apps).sort();
  }, [providers, selected]);

  // Every process name any catalogue provider owns — used to tell "managed by
  // this screen" apart from custom process names typed on the Endpoint Agents
  // screen, so saving here never wipes those.
  const catalogueApps = useMemo(
    () => new Set(providers.flatMap((p) => p.apps || [])),
    [providers]
  );

  const save = async () => {
    setSaving(true);
    setSavedNotice(null);
    // Keep any custom, catalogue-external process names the Endpoint Agents
    // screen set; replace only the catalogue-managed ones with the selection.
    const preserved = currentApps.filter((a) => !catalogueApps.has(a));
    const appsToSave = Array.from(new Set([...selectedApps, ...preserved]));
    try {
      const r = await fetch(`${apiUrl}/admin/bdr/policy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, apps: appsToSave, endpoints: selectedDomains }),
      });
      if (r.ok) {
        setSavedNotice(`Saved — ${selected.size} app${selected.size === 1 ? '' : 's'}, ${selectedDomains.length} domain${selectedDomains.length === 1 ? '' : 's'} under "${action}".`);
      } else {
        setSavedNotice('Save failed — see console.');
      }
    } catch (err) {
      console.error(err);
      setSavedNotice('Save failed — see console.');
    } finally {
      setSaving(false);
    }
  };

  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  if (loading) {
    return (
      <div className="page-content">
        <div className="ent-empty">{t('pages.aiAppPolicy.loading')}</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>{t('pages.aiAppPolicy.aiAppPolicy')}</h1>
          <p className="subtitle">{t('pages.aiAppPolicy.allowWarnOrBlockAIApplications')}</p>
        </div>
      </div>

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{t('pages.aiAppPolicy.actionForSelectedApps')}</div>
        <div className="ent-card-body">
          <div className="ent-toolbar" style={{ marginTop: 0 }}>
            {ACTIONS.map((a) => (
              <button key={a} className={`ent-btn ${action === a ? 'primary' : ''}`} onClick={() => setAction(a)}>
                {cap(a)}
              </button>
            ))}
            <button className="ent-btn primary" onClick={save} disabled={saving} style={{ marginLeft: 'auto' }}>
              {saving ? t('pages.aiAppPolicy.saving') : t('pages.aiAppPolicy.save')}
            </button>
          </div>
          <div className={`notice ${action === 'block' ? 'error' : action === 'log' ? 'info' : 'warn'}`} style={{ marginTop: 12 }}>
            {action === 'log' && t('pages.aiAppPolicy.actionLogHint')}
            {action === 'alert' && t('pages.aiAppPolicy.actionAlertHint')}
            {action === 'block' && t('pages.aiAppPolicy.actionBlockHint')}
          </div>
          {savedNotice && <div className="notice success" style={{ marginTop: 8 }}>{savedNotice}</div>}
          <div className="notice info" style={{ marginTop: 8 }}>
            {t('pages.aiAppPolicy.oneActionAppliesToEveryApp')}
          </div>
        </div>
      </div>

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">{t('pages.aiAppPolicy.createPolicyFromASentence')}</div>
        <div className="ent-card-body">
          <textarea
            className="ent-textarea"
            placeholder={t('pages.aiAppPolicy.eGBlockAllUnsanctionedAI')}
            value={commandText}
            onChange={(e) => setCommandText(e.target.value)}
            rows={2}
          />
          <div className="ent-toolbar">
            <button className="ent-btn primary" onClick={runParseCommand} disabled={parsing || !commandText.trim()}>
              {parsing ? t('pages.aiAppPolicy.parsing') : t('pages.aiAppPolicy.parse')}
            </button>
          </div>
          {parseError && <div className="notice error" style={{ marginTop: 8 }}>{parseError}</div>}
          {parseResult && (
            <div className="notice info" style={{ marginTop: 8 }}>
              <div style={{ marginBottom: 8 }}>
                <span className={`badge ${parseResult.action === 'block' ? 'badge-error' : parseResult.action === 'alert' ? 'badge-unknown' : 'badge-info'}`}>
                  {cap(parseResult.action)}
                </span>
                <span style={{ marginLeft: 8 }}>
                  {parseResult.providerIds.length === 0
                    ? t('pages.aiAppPolicy.noAppsMatched')
                    : parseResult.providerIds
                        .map((id) => providers.find((p) => p.id === id)?.label || id)
                        .join(', ')}
                </span>
              </div>
              {parseResult.warnings.length > 0 && (
                <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
                  {parseResult.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}
              <button
                className="ent-btn primary sm"
                onClick={applyParseResult}
                disabled={parseResult.providerIds.length === 0}
              >
                {t('pages.aiAppPolicy.applyToSelection')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="ent-card">
        <div className="ent-card-head">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>{t('pages.aiAppPolicy.aiApplications', { v0: providers.length })}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ent-btn sm" onClick={() => selectByRisk('unsanctioned')}>{t('pages.aiAppPolicy.selectUnsanctioned')}</button>
              <button className="ent-btn sm" onClick={() => selectByRisk('high_risk')}>{t('pages.aiAppPolicy.selectHighRisk')}</button>
              <button className="ent-btn sm" onClick={clearSelection}>{t('pages.aiAppPolicy.clear')}</button>
            </div>
          </div>
        </div>
        <div className="ent-card-body">
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>{t('pages.aiAppPolicy.application')}</th>
                  <th style={{ width: 90 }}>{t('pages.aiAppPolicy.type')}</th>
                  <th style={{ width: 110 }}>{t('pages.aiAppPolicy.risk')}</th>
                  <th>{t('pages.aiAppPolicy.domains')}</th>
                </tr>
              </thead>
              <tbody>
                {providers.length === 0 && (
                  <tr><td colSpan={5} className="ent-empty">{t('pages.aiAppPolicy.noProvidersInCatalogue')}</td></tr>
                )}
                {providers.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                    </td>
                    <td>
                      <strong>{p.label}</strong>
                      {p.kind === 'embedded' && <span style={{ color: 'var(--text-2)', fontSize: 11 }}> {t('pages.aiAppPolicy.embedded')}</span>}
                    </td>
                    <td><span className={`badge ${p.type === 'mcp' ? 'badge-info' : 'badge-redact'}`}>{p.type || t('pages.aiAppPolicy.llm')}</span></td>
                    <td>
                      {p.riskLevel && (
                        <span className={`badge ${RISK_BADGE[p.riskLevel] || 'badge-unknown'}`}>
                          {RISK_LABEL[p.riskLevel] || p.riskLevel}
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-2)', fontSize: 12 }}>
                      {p.match.join(', ')}
                      {p.apps && p.apps.length > 0 && (
                        <div style={{ marginTop: 2 }}>
                          <span className="badge badge-error" style={{ fontSize: 10 }}>
                            {t('pages.aiAppPolicy.terminates')} {p.apps.join(', ')}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiAppPolicy;
